/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { GlobalConstants } from '../../constants/global-constants';
import { FittingTreeViewComponent } from '../add-fitting-tab/fitting-tree-view/fitting-tree-view.component';

import { AddFittingTabComponent } from '../add-fitting-tab/add-fitting-tab.component';
import { Item } from 'libs/sysque/toolbelt/data-access/src/lib/domain/item';
import { Subscription } from 'rxjs';
import {
  ADD_FITTING_MODE_FACADE_TOKEN,
  IAddFittingModeFacade,
} from '../../../../../data-access/src/lib/domain/facades-models/add-fitting-mode-facade-interface';
import { FittingTreeView } from '../../models/fitting-tree-view-vm';
import { PastedItemsInfo } from '../../models/pasted-items-info';
import { AddFittingPanelFrameComponent } from '../add-fitting-panel-frame/add-fitting-panel-frame.component';
import { SysQueCopyPasteService } from '../../../../../utils/src/lib/sysque-copy-paste/sysque-copy-paste.service';
import { AlertStatus, IAlert } from '../../../../../../shared/ui/src/lib/components/alert/alert-types';

@Component({
  selector: 'sysque-add-fitting-mode',
  templateUrl: './add-fitting-mode.component.html',
  styleUrls: ['./add-fitting-mode.component.scss'],
  standalone: false
})
export class AddFittingModeComponent implements OnInit, OnDestroy {
  public warningMessage: string = '';
  public alertStatus = AlertStatus.Warning;
  public fittingTreeView: FittingTreeViewComponent;
  public subscription: Subscription;
  public componentTitle = '';

  @Input() isManageMode: boolean = false;
  @Output() notifyDoneClick: EventEmitter<void> = new EventEmitter();
  @ViewChild('tabPanel') tabPanel: AddFittingTabComponent;

  public dialogTitle = '';
  public dialogContent = '';
  public replaceAll = false;
  public showDialog = false;
  public duplicateItemList: PastedItemsInfo[];
  public duplicateListIndex = 0;
  public itemName: string = '';
  public currentItem: Item;
  public srcSelectedSystem: string;
  public destSelectedSystem: string;
  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: true,
    };
  }

  @ViewChild(AddFittingPanelFrameComponent) child: AddFittingPanelFrameComponent;

  constructor(
    @Inject(ADD_FITTING_MODE_FACADE_TOKEN) public facade: IAddFittingModeFacade,
    private cdr: ChangeDetectorRef,
    public copyPasteService: SysQueCopyPasteService
  ) {}

  public ngOnInit(): void {
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
  }

  public ngOnDestroy(): void {
    window.removeEventListener('storage', this.handleStorageEvent.bind(this));
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  public handleStorageEvent(event: StorageEvent): void {
    if (event.newValue === '') {
      return;
    }

    if (event.key === GlobalConstants.ADD_TO_SYSTEM_ALERT) {
      this.warningMessage = JSON.parse(event.newValue);
    }

    if (event.key === GlobalConstants.DuplicateItemsUpdate) {
      const response = JSON.parse(event.newValue);
      if (response) {
        this.onShowDuplicateError(this.duplicateItemList);
      }
    }

    this.cdr.detectChanges();
  }

  public onDismissClick(): void {
    this.warningMessage = '';
  }

  public onDoneClick(): void {
    this.facade.updateLocalStorage(GlobalConstants.DockableKey, true);
    this.notifyDoneClick.emit();
  }

  //#region Copy Paste logic

  public onPasteError(event: string): void {
    this.warningMessage = event;
    this.alertStatus = AlertStatus.Warning;
    this.showDialog = false;
  }

  public onShowDuplicateError(event: PastedItemsInfo[]): void {
    if (this.duplicateItemList && this.duplicateListIndex > this.duplicateItemList.length - 1) {
      this.executePasteLogic();
      return;
    }
    if (!this.duplicateItemList) {
      this.copyPasteService.setDuplicateItemsList([]);
      this.facade.updateLocalStorage(GlobalConstants.CanExecutePasteLogic, false);
      this.duplicateItemList = event;
      this.replaceAll = false;
    }

    if (!this.replaceAll) {
      this.getAlertInfo();
    }
  }

  public executePasteLogic(): void {
    this.duplicateItemList = this.duplicateItemList.filter((item) => !item.isCancel);
    if (this.duplicateItemList.length > 0) {
      this.copyPasteService.setDuplicateItemsList(this.duplicateItemList);
      this.facade.updateLocalStorage(GlobalConstants.CanExecutePasteLogic, true);

      const item = this.copyPasteService.getUniqueCopiedItemsList();
      if (item.length > 0) {
        item.forEach((item) => {
          this.duplicateItemList.push(item);
        });
      }

      this.facade.executePasteItemsAction(this.duplicateItemList);
    }
    this.duplicateItemList = null;
    this.duplicateListIndex = 0;
    this.replaceAll = false;
    this.child.updateStatus();
    this.cdr.detectChanges();
  }

  public getAlertInfo(): void {
    this.itemName = this.duplicateItemList[this.duplicateListIndex].copiedItem.name;
    this.currentItem = this.duplicateItemList[this.duplicateListIndex].copiedItem;
    this.srcSelectedSystem = this.duplicateItemList[this.duplicateListIndex].srcSystemName;
    this.destSelectedSystem = this.duplicateItemList[this.duplicateListIndex].destSystemName;
    this.dialogTitle = GlobalConstants.ReplaceItemTitle;
    this.dialogContent = GlobalConstants.ReplaceItemContent + this.itemName + ' in ' + this.destSelectedSystem + '?';
    this.showDialog = true;
    this.duplicateListIndex++;
  }

  public onReplaceClick(): void {
    if (this.replaceAll) {
      this.updateItemsOnReplaceAllCheck();
    } else {
      this.updateItemsOnReplaceClick();
    }
  }

  public updateItemsOnReplaceClick(): void {
    this.showDialog = false;
    let indexToUpdate = this.duplicateItemList.findIndex((item) => item.copiedItem.name === this.itemName);
    this.duplicateItemList[indexToUpdate].isReplace = true;
    this.copyPasteService.setDuplicateItemsList(this.duplicateItemList);
    this.onShowDuplicateError(this.duplicateItemList);
  }

  public onCancelClick(): void {
    this.showDialog = false;
    let indexToUpdate = this.duplicateItemList.findIndex((item) => item.copiedItem.name === this.itemName);
    this.duplicateItemList[indexToUpdate].isCancel = true;
    if (this.replaceAll) {
      this.replaceAll = false;
    }
    this.child.removeItem(this.currentItem);
  }

  public onReplaceAll(event: any): void {
    this.replaceAll = event.detail;
  }

  public updateItemsOnReplaceAllCheck(): void {
    this.showDialog = false;
    this.duplicateItemList = this.duplicateItemList.map((item) => {
      if (item.isDuplicate) {
        return { ...item, isReplaceAll: true };
      } else {
        return item;
      }
    });
    this.executePasteLogic();
  }

  //#endregion

  async onShowInEnclosingFolder(item: Item): Promise<void> {
    this.switchTab();
    this.subscription = this.getTreeViewInstance();
    this.fittingTreeView.treeView.instance.on('itemExpanded', (e) =>
      this.fittingTreeView.handleItemAction(e, 'expand')
    );
    this.fittingTreeView.treeView.instance.on('itemCollapsed', (e) =>
      this.fittingTreeView.handleItemAction(e, 'collapse')
    );
    let parentItem = this.getParentFolder(item);
    if (!parentItem) {
      return;
    }
    await this.expandItem(parentItem);
    const itemName = item.itemPath.split('\\').pop().toLocaleLowerCase();
    const selectedItem = this.selectItemBasedOnName(parentItem, itemName);
    this.fittingTreeView.showInEnclosingFolder(selectedItem);
  }

  private switchTab(): void {
    this.tabPanel.addFittingtabPanel.dxTabPanel.instance.option('selectedIndex', 1);
  }

  private getTreeViewInstance(): Subscription {
    return this.facade.getFittingTreeViewInstance().subscribe((fittingTreeView: FittingTreeViewComponent) => {
      this.fittingTreeView = fittingTreeView;
    });
  }

  private getParentFolder(item: Item): FittingTreeView {
    const itemFolderPath = item.itemPath.split('\\').slice(0, -1).join('\\');
    let parentItem = this.facade.getTreeViewItem(this.fittingTreeView.dataSource, itemFolderPath);
    return parentItem;
  }

  private async expandItem(item: FittingTreeView): Promise<void> {
    await this.fittingTreeView.expandItem(item);
    this.fittingTreeView.treeView.instance.option('items', this.fittingTreeView.dataSource);
  }

  private selectItemBasedOnName(item: FittingTreeView, name: string): FittingTreeView {
    const selectedItem = item.items.find((x) => x?.text?.toLocaleLowerCase() === name);
    this.fittingTreeView.onItemClick(selectedItem);
    this.fittingTreeView.treeView.instance.selectItem(selectedItem);
    return selectedItem;
  }
}
